import { useState, ChangeEvent } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  CircularProgress,
  Alert
} from '@mui/material';
// import { useLogRocket } from '../../hooks/useLogRocket';
import { chatApi } from 'api';
import { personalDataStore } from 'stores/personalDataStore';

interface LogRocketFeedbackDialogProps {
  open: boolean;
  onClose: () => void;
}

const EMAIL_REGEXP = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const LogRocketFeedbackDialogComponent = ({ open, onClose }: LogRocketFeedbackDialogProps) => {
  const { isAuthenticated, user } = personalDataStore;

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleTitleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
    setError(null);
  };

  const handleDescriptionChange = (e: ChangeEvent<HTMLInputElement>) => {
    setDescription(e.target.value);
    setError(null);
  };

  const handleGuestNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setGuestName(e.target.value);
    setError(null);
  };

  const handleGuestEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    setGuestEmail(e.target.value);
    setError(null);
  };

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setGuestName('');
    setGuestEmail('');
  };

  const handleSubmit = () => {
    if (!title.trim()) {
      setError('Пожалуйста, укажите заголовок');
      return;
    }

    if (!description.trim()) {
      setError('Пожалуйста, укажите описание');
      return;
    }

    if (!isAuthenticated) {
      if (!guestName.trim()) {
        setError('Пожалуйста, укажите ваше имя');
        return;
      }

      if (!guestEmail.trim() || !EMAIL_REGEXP.test(guestEmail.trim())) {
        setError('Пожалуйста, укажите корректный email');
        return;
      }
    }

    setError(null);
    setSuccess(false);
    handleSendMessage();
  };

  const handleClose = () => {
    if (!isSubmitting) {
      resetForm();
      setError(null);
      setSuccess(false);
      onClose();
    }
  };

  const handleSendMessage = async () => {
    try {
      setIsSubmitting(true);
      const text = `${title.trim()}\n ${description.trim()}\n`;

      if (isAuthenticated) {
        const conversation = await chatApi.findOrCreateConversationServiceUser();
        await chatApi.sendMessage(conversation.id, text);
      } else {
        const name =
          guestName.trim() ||
          [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() ||
          'Гость';
        const email = guestEmail.trim() || user?.email || '';

        await chatApi.sendGuestSupportMessage({
          email,
          name,
          text
        });
      }

      setSuccess(true);
      resetForm();
      setTimeout(() => {
        setSuccess(false);
        onClose();
      }, 1500);
    } catch (e) {
      console.log(e);
      setError('Не удалось отправить сообщение. Попробуйте еще раз.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isSubmitDisabled =
    isSubmitting ||
    !title.trim() ||
    !description.trim() ||
    (!isAuthenticated && (!guestName.trim() || !guestEmail.trim()));

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography sx={{ mb: 4 }} variant="h6">Обращение в поддержку</Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {success && (
            <Alert severity="success">Сообщение успешно отправлено!</Alert>
          )}
          {error && (
            <Alert severity="error">{error}</Alert>
          )}
          {!isAuthenticated && (
            <>
              <TextField
                fullWidth
                label="Ваше имя"
                placeholder="Как к вам обращаться"
                value={guestName}
                onChange={handleGuestNameChange}
                disabled={isSubmitting}
                required
                inputProps={{ 'aria-label': 'Ваше имя' }}
              />
              <TextField
                fullWidth
                type="email"
                label="Email для ответа"
                placeholder="name@example.com"
                value={guestEmail}
                onChange={handleGuestEmailChange}
                disabled={isSubmitting}
                required
                inputProps={{ 'aria-label': 'Email для ответа' }}
              />
            </>
          )}
          <TextField
            fullWidth
            label="Заголовок"
            placeholder="Краткое описание проблемы или вопроса"
            value={title}
            onChange={handleTitleChange}
            disabled={isSubmitting}
            required
            inputProps={{ 'aria-label': 'Заголовок сообщения' }}
          />
          <TextField
            fullWidth
            multiline
            rows={6}
            label="Текст сообщения"
            placeholder="Подробно опишите проблему, вопрос или предложение"
            value={description}
            onChange={handleDescriptionChange}
            disabled={isSubmitting}
            required
            inputProps={{ 'aria-label': 'Описание сообщения' }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Отмена
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitDisabled}
          startIcon={isSubmitting ? <CircularProgress size={16} /> : null}
        >
          {isSubmitting ? 'Отправка...' : 'Отправить'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const LogRocketFeedbackDialog = observer(LogRocketFeedbackDialogComponent);

export default LogRocketFeedbackDialog;
